import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  color: var(--text-primary);
  padding: 60px 0 20px;
  border-top: 2px solid var(--brand-blue);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-bottom: 40px;
`;

const FooterSection = styled.div`
  h3 {
    color: var(--brand-blue);
    margin-bottom: 16px;
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  p {
    color: var(--text-secondary);
    margin-bottom: 8px;
    line-height: 1.6;
  }
  
  a {
    color: var(--text-secondary);
    text-decoration: none;
    display: block;
    margin-bottom: 8px;
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--brand-blue);
    }
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  img {
    width: 50px;
    height: 50px;
    border-radius: 8px;
    object-fit: contain;
  }
  
  .logo-text {
    h3 {
      color: var(--brand-blue);
      margin: 0 0 4px 0;
      font-size: 1.2rem;
      font-weight: 700;
    }
    
    p {
      color: var(--text-secondary);
      margin: 0;
      font-size: 0.9rem;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;

  a {
    width: 40px;
    height: 40px;
    background-color: #043663;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease, transform 0.2s ease;

    &,
    &:hover,
    &:focus {
      color: #ffffff;
    }

    svg {
      fill: #ffffff;
    }

    &:hover {
      background-color: #0a4d7a;
      transform: translateY(-3px);

      svg {
        fill: #ffffff;
      }
    }
  }
`;

const FooterBottom = styled.div`
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
  color: #4b5563;
  font-size: 0.8125rem;
  letter-spacing: 0.01em;

  p {
    margin: 0;
  }
`;

const FooterCreditDivider = styled.span`
  display: inline-block;
  width: 3px;
  height: 3px;
  margin: 0 10px 1px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.45;
  vertical-align: middle;
`;

const PoweredByLink = styled.a`
  color: inherit;
  font-weight: 500;
  text-decoration: none;
  border-bottom: 1px solid rgba(75, 85, 99, 0.45);
  padding-bottom: 1px;

  &:hover {
    color: var(--brand-blue);
    border-bottom-color: var(--brand-blue);
  }
`;

const Footer = () => {
  const { t } = useTranslation();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get current year from server or use local year
    setCurrentYear(new Date().getFullYear());
  }, []);

  const handleHomeClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to home page and scroll to top
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <LogoSection>
            <img src="/images/logo/logo.png" alt={t('header.logoAlt')} />
            <div className="logo-text">
              <h3>{t('hero.title')}</h3>
              <p>{t('footer.subtitle')}</p>
            </div>
          </LogoSection>
          <p>{t('footer.description')}</p>
        </FooterSection>
        
        <FooterSection>
          <h3>{t('footer.quickLinks')}</h3>
          <Link to="#" onClick={handleHomeClick}>{t('footer.home')}</Link>
          <Link to="/guide-to-hps">{t('footer.guideToHps')}</Link>
          <Link to="/partner-clubs">{t('footer.partnerships')}</Link>
          <Link to="/terms-and-conditions">{t('footer.termsAndConditions')}</Link>
          <Link to="/privacy-policy">{t('footer.privacyPolicy')}</Link>
        </FooterSection>
        
        <FooterSection>
          <h3>{t('footer.followUs')}</h3>
          <SocialLinks>
            <a href="https://www.instagram.com/helveticpadel/" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61579743462563" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </SocialLinks>
        </FooterSection>
      </FooterContent>
      
      <FooterBottom>
        <p>
          <span>&copy; {currentYear} {t('footer.rights')}</span>
          <FooterCreditDivider aria-hidden="true" />
          <PoweredByLink href="https://kamaansolutions.com/ref/helvetic" target="_blank" rel="noopener noreferrer">
            {t('footer.poweredBy')}
          </PoweredByLink>
        </p>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;